import { API_BASE_URL } from '@/config/site'
import { showAuthDialog } from '@/store/authDialog'
import { clearStoredSession, getToken } from '@/utils/auth'

const joinUrl = (baseUrl, path) => {
  if (!baseUrl) {
    return path
  }

  const normalizedBase = baseUrl.replace(/\/+$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  return `${normalizedBase}${normalizedPath}`
}

const parseResponse = async (response) => {
  const contentType = response.headers.get('content-type') || ''

  if (!contentType.includes('application/json')) {
    throw new Error('接口返回的不是 JSON 数据，请检查后端服务是否可用。')
  }

  return response.json()
}

const AUTH_ERROR_CODES = [401, 50008, 50012, 50014]
const AUTH_ERROR_KEYWORDS = ['请登录', '重新登录', '未登录', '登录失效', '登录已失效', '登录过期', '登录已过期', '令牌失效', '令牌已失效', '令牌过期', '令牌已过期', '认证', '授权', 'token', 'auth', 'unauthorized']

const isAuthErrorResult = (result = {}, response) => {
  const code = Number(result.code || response.status || 0)
  const message = String(result.msg || result.message || '').toLowerCase()

  return response.status === 401 ||
    AUTH_ERROR_CODES.includes(code) ||
    AUTH_ERROR_KEYWORDS.some((keyword) => message.includes(keyword.toLowerCase()))
}

const notifyAuthExpired = (message) => {
  clearStoredSession()
  showAuthDialog({
    message: message || '登录已失效，请重新登录'
  })

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('course-auth-expired', {
      detail: {
        message: message || '登录已失效，请重新登录'
      }
    }))
  }
}

const request = async (path, options = {}) => {
  const {
    method = 'POST',
    data = {},
    headers = {},
    timeout = 15000
  } = options

  const controller = typeof AbortController === 'undefined' ? null : new AbortController()
  let timeoutId = null

  if (controller) {
    timeoutId = window.setTimeout(() => {
      controller.abort()
    }, timeout)
  }

  try {
    const token = getToken()
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData
    const response = await fetch(joinUrl(API_BASE_URL, path), {
      method,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `bearer ${token}` } : {}),
        ...headers
      },
      body: method.toUpperCase() === 'GET'
        ? undefined
        : (isFormData ? data : JSON.stringify(data)),
      signal: controller ? controller.signal : undefined
    })

    const result = await parseResponse(response)

    if (isAuthErrorResult(result, response)) {
      notifyAuthExpired(result.msg || '登录已失效，请重新登录')
      throw new Error(result.msg || '登录已失效，请重新登录')
    }

    if (!response.ok) {
      throw new Error(result.msg || `请求失败（${response.status}）`)
    }

    if (Number(result.code) !== 200) {
      throw new Error(result.msg || '请求失败')
    }

    return result
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('请求超时，请稍后重试。')
    }

    throw error
  } finally {
    if (timeoutId) {
      window.clearTimeout(timeoutId)
    }
  }
}

export default request
